import { useState } from 'react';

import { useRecordModal } from 'hooks/useRecordModal';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { TranslationKey } from 'providers/i18n/types';
import { ListContextProvider, useList } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { PageHeader } from 'shared/mui/PageHeader';
import { GatewayConversionListItem } from 'types/embedded/orderConversion';

import { useOrderConversion } from './hooks/useOrderConversion';
import { useOrderConversionFilter } from './hooks/useOrderConversionFilter';
import { OrderConversionListSummaryStyled } from './OrderConversionList.styled';
import { OrderConversionListConversionChart } from './OrderConversionListConversionChart/OrderConversionListConversionChart';
import { OrderConversionListDatagrid } from './OrderConversionListDatagrid/OrderConversionListDatagrid';
import { OrderConversionListFilter } from './OrderConversionListFilter/OrderConversionListFilter';
import { OrderConversionListGatewayCreate } from './OrderConversionListGateway/OrderConversionListGatewayCreate/OrderConversionListGatewayCreate';
import { OrderConversionListGatewayDelete } from './OrderConversionListGateway/OrderConversionListGatewayDelete/OrderConversionListGatewayDelete';
import { OrderConversionListGatewayEdit } from './OrderConversionListGateway/OrderConversionListGatewayEdit/OrderConversionListGatewayEdit';
import { OrderConversionListGatewayReferenceDialog } from './OrderConversionListGateway/OrderConversionListGatewayReferenceDialog/OrderConversionListGatewayReferenceDialog';
import { OrderConversionListOrders } from './OrderConversionListOrders/OrderConversionListOrders';
import { OrderConversionListSkeleton } from './OrderConversionListSkeleton';

export const OrderConversionList = () => {
  const translate = useTypedTranslate();
  const [isOpenReferenceDialog, setIsOpenReferenceDialog] = useState(false);

  const { form, params, ...filterProps } = useOrderConversionFilter();
  const { data, isLoading } = useOrderConversion(params);

  const {
    record: recordCreate,
    handleOpenRecordModal: handleOpenCreateModal,
    openRecordModal: openCreateModal,
    handleCloseRecordModal: handleCloseCreateModal,
  } = useRecordModal<GatewayConversionListItem>();

  const {
    record: recordEdit,
    handleOpenRecordModal: handleOpenEditModal,
    openRecordModal: openEditModal,
    handleCloseRecordModal: handleCloseEditModal,
  } = useRecordModal<GatewayConversionListItem>();

  const {
    record: recordDelete,
    handleOpenRecordModal: handleOpenDeleteModal,
    openRecordModal: openDeleteModal,
    handleCloseRecordModal: handleCloseDeleteModal,
  } = useRecordModal<GatewayConversionListItem>();

  const openReferenceDialog = () => setIsOpenReferenceDialog(true);

  const listContext = useList({
    data: data?.gatewayConversionList || [],
  });

  const showGatewayConversionSuccessAlert = !!data?.gatewayConversionList.length;
  const showOrderConversionSuccessAlert =
    !!data?.totalOrders && !!data?.summaryConversionItems.length;

  return (
    <ListContextProvider value={listContext}>
      <div>
        <PageHeader
          actions={[]}
          headerTitle={{
            titleText: translate('app.orderConversion.orderConversion'),
            tooltipText: translate('app.orderConversion.orderConversionMetricDescription'),
          }}
          sx={{ marginTop: '24px' }}
        />
        <OrderConversionListFilter form={form} {...filterProps} />
        {isLoading && <OrderConversionListSkeleton />}
        {!isLoading && (
          <>
            {!data ? (
              <Alert severity="info">
                {translate('app.orderConversion.filterSelectMerchantToViewStats')}
              </Alert>
            ) : (
              <>
                {showOrderConversionSuccessAlert && (
                  <Alert severity="success">
                    {translate('app.orderConversion.orderConverionSuccessAlert', {
                      period: translate(
                        `app.orderConversion.hours${params.period}` as TranslationKey,
                      ),
                    })}
                  </Alert>
                )}
                <OrderConversionListSummaryStyled>
                  <OrderConversionListOrders orders={data?.totalOrders} />
                  <OrderConversionListConversionChart
                    items={data?.summaryConversionItems || []}
                    totalGroups={data?.totalOrders.groups || {}}
                  />
                </OrderConversionListSummaryStyled>
                <PageHeader
                  headerTitle={{
                    titleText: translate('app.orderConversion.gatewaysList'),
                    titleSize: 'medium',
                  }}
                  sx={{ marginTop: '21px' }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {showGatewayConversionSuccessAlert && (
                    <Alert severity="success">
                      {translate('app.orderConversion.gatewayConverionSuccessAlert', {
                        period: translate(
                          `app.orderConversion.hours${params.period}` as TranslationKey,
                        ),
                      })}
                    </Alert>
                  )}
                  <OrderConversionListDatagrid
                    handleOpenCreateModal={handleOpenCreateModal}
                    handleOpenEditModal={handleOpenEditModal}
                  />
                </Box>
              </>
            )}
          </>
        )}
        {openCreateModal && (
          <OrderConversionListGatewayCreate
            merchant={form.getValues('merchant')}
            onClose={handleCloseCreateModal}
            onOpenReferenceDialog={openReferenceDialog}
            open={openCreateModal}
            record={recordCreate}
          />
        )}
        {openEditModal && (
          <OrderConversionListGatewayEdit
            onClose={handleCloseEditModal}
            onDelete={(record) => {
              handleOpenDeleteModal(record);
              handleCloseEditModal();
            }}
            onOpenReferenceDialog={openReferenceDialog}
            open={openEditModal}
            params={params}
            record={recordEdit}
          />
        )}
        <OrderConversionListGatewayDelete
          onCancel={(record) => {
            handleOpenEditModal(record);
            handleCloseDeleteModal();
          }}
          onClose={handleCloseDeleteModal}
          open={openDeleteModal}
          record={recordDelete}
        />
        {isOpenReferenceDialog && (
          <OrderConversionListGatewayReferenceDialog
            onClose={() => setIsOpenReferenceDialog(false)}
            open={isOpenReferenceDialog}
          />
        )}
      </div>
    </ListContextProvider>
  );
};
